import type { MatchState, PlayerState } from '../sim/state';
import type { SimConfig } from '../config/simConfig';
import type { PlayerInput, EntityId, Vec2 } from '../types';
import { PITCH } from '../config/pitchConfig';
import { BTN } from '../sim/input';
import { xg } from './xg';
import type { TeamPlan } from './plan';
import { refClarity } from '../referee/detect';
import { inOwnBox } from '../sim/gk';
import { makeLatch, rollOpportunity, type Latch } from './misconduct';
import { inCeremony } from '../rules/phase';
import { passChargeFor } from '../sim/passing';
import { ballLanding } from '../sim/aerial';
import { rngFloat, type RngHolder } from '../math/rng';
import { IDENTITY_PROFILE, aimNoise, passElevNoise, type DifficultyProfile } from './difficulty';
import { decideCarrier, spaceAhead as spaceAheadOf, type CarrierChoice } from './utilityCarrier';
import { wallSpotFor } from './wall';

// actPlayer (§8.1): a cheap per-tick decision list producing a PlayerInput for one bot. Ordered evaluators:
// (1) hasBall → shoot / pass / dribble, (2) chaser → press + stand-tackle, (5) default → formation shape,
// face ball, CALL when open. Retaliation/dive/tactical-foul (evaluators 3–4) are M5. Kicks charge over
// several ticks then release — the charge latch in BotMemory keeps the decision stable mid-windup. Pure:
// reads state, uses only the passed rng, returns an input.

export interface BotMemory {
  kick: { button: number; aim: number; chargeTarget: number; aimElev?: number } | null; // in-progress charged kick (aimElev: D46 keeper lofted punt)
  lastAggressor: { id: EntityId; tick: number } | null; // who last fouled me (grudge, §8.1)
  gkReact: number | null; // keeper's sampled reaction delay for the current incoming shot (null when idle)
  gkHolding: boolean; // D125 — arc-hold hysteresis: is he currently moving to his spot? (GK_HOLD_BAND)
  gkApproaching: boolean; // D125 — 'a shot is coming' latch, with its own enter/exit thresholds
  gkPenaltyGuess: number | null; // D88: on a penalty the keeper GUESSES a side (±1) before the kick (can't react to a point-blank spot-kick) — null when no penalty pending
  tackleReadyTick: number | null; // D32: first tick the exposed-ball stand tackle may fire (null = re-armed)
  util: { choice: CarrierChoice; until: number; readyAt: number } | null; // D57-A: the standing utility decision (inertia + commit window); D115 readyAt: the tick a CHANGED decision may fire
  lastFeeder: { id: EntityId; tick: number } | null; // D59: last seen kicker of a ball I didn't kick (kickedBy clears on the claim)
  gainTick: number | null; // D59: when the current possession spell started (null = not on the ball)
  lastHolder: EntityId | null; // D115: the last player who actually HELD the ball — tells a knock-on from a genuine new reception
  receivedFrom: EntityId | null; // D59: the TEAMMATE whose pass started this spell — return passes to him are suppressed
  latches: { retaliate: Latch; tacticalFoul: Latch; dive: Latch }; // misconduct opportunity latches (§8.1)
  // D136 — three new latches. All three are created by `makeBotMemory` and nothing else, which is what
  // keeps the D62 hazard closed: a human takeover replaces the whole memory, so none of them can go
  // stale across a change of driver.
  trapReadyTick: number | null; // D136: the tick a bot's TRAP may fire (a beat, not a reflex)
  bargeRoll: { approachTick: number; go: boolean } | null; // D136: ONE P_BARGE roll per approach, latched
  duelMissUntil: number | null; // D136: this armed tackle mistimed — re-arm rather than press
}
export function makeBotMemory(): BotMemory {
  return { kick: null, lastAggressor: null, gkReact: null, gkHolding: false, gkApproaching: false, gkPenaltyGuess: null, tackleReadyTick: null, util: null, lastFeeder: null, gainTick: null, lastHolder: null, receivedFrom: null, latches: { retaliate: makeLatch(), tacticalFoul: makeLatch(), dive: makeLatch() }, trapReadyTick: null, bargeRoll: null, duelMissUntil: null };
}

/** D59: possession-spell bookkeeping, shared by outfielders AND the keeper. kickedBy clears on the
 *  claim, so the feeder is remembered from the FLIGHT (the last kicker of a ball that wasn't mine);
 *  the gain edge then stamps `receivedFrom` when that feed was a recent teammate's. Call it BEFORE
 *  any early return — the gain edge must never be missed.
 *
 *  D115: the edge is now keyed on `lastHolder`, not on possession going null. The D39 dribble knock-on
 *  releases possession for a few ticks every DRIBBLE_KNOCK_PERIOD, and treating each re-claim as a
 *  fresh reception would re-arm the settle window on a metronome — a sprinting carrier could never
 *  finish. A spell ends only when someone ELSE last held the ball. */
export function trackPossessionSpell(state: MatchState, p: PlayerState, mem: BotMemory, cfg: SimConfig): void {
  const ball = state.ball;
  if (ball.kickedBy !== null && ball.kickedBy !== p.id) mem.lastFeeder = { id: ball.kickedBy, tick: state.tick };
  if (ball.possessor === p.id) {
    if (mem.lastHolder !== p.id || mem.gainTick === null) {
      mem.gainTick = state.tick;
      const f = mem.lastFeeder;
      const feeder = f !== null && state.tick - f.tick <= cfg.ai.UTIL.RETURN_TICKS ? state.players[f.id] : undefined;
      mem.receivedFrom = feeder !== undefined && feeder.team === p.team && !feeder.sentOff ? feeder.id : null;
    }
  } else if (ball.possessor !== null || mem.lastHolder !== p.id) {
    mem.gainTick = null; // someone else has it, or the loose ball came off someone else — my spell is over
  }
  if (ball.possessor !== null) mem.lastHolder = ball.possessor;
}

/** D115: this player's first-touch window for the current spell, in ticks. Profile-scaled (`reactionMul`)
 *  and jittered per (player, spell) so a team never releases on one beat. The jitter is DERIVED from the
 *  spell's own gain tick rather than drawn from the AI rng — same non-metronomic feel, and the §7 draw
 *  order (and every pinned bot golden) stays exactly where it was. */
export function settleTicks(id: EntityId, gainTick: number, prof: DifficultyProfile, cfg: SimConfig): number {
  const span = cfg.ai.SETTLE_JITTER * 2 + 1;
  const jitter = (((id * 7 + gainTick) % span) + span) % span; // 0 … 2·JITTER
  return Math.max(0, Math.round(cfg.ai.SETTLE_TICKS * prof.reactionMul) + jitter - cfg.ai.SETTLE_JITTER);
}

const SHOOT_CHARGE = 12;
const ARRIVE_DIST = 0.6;
// D123 — how far out of shape a non-taker must be before the ceremony walks him back. Deliberately
// coarse: the point is to fix players stranded where the whistle caught them, NOT to micro-adjust
// everyone. A tight threshold marched the whole team onto its slots and pulled the taker's nearest
// options away from him — measurably (it halved the 3v3 give-and-go feed rate, D45's kickabout test).
const RESTART_SHAPE_DIST = 4.0;

const angleTo = (from: Vec2, to: Vec2): number => Math.atan2(to.y - from.y, to.x - from.x);
const dist = (a: Vec2, b: Vec2): number => Math.hypot(a.x - b.x, a.y - b.y);
const input = (mv: Vec2, aim: number, buttons: number): PlayerInput => ({ moveX: mv.x, moveY: mv.y, aimYaw: aim, aimElev: 0, buttons, emote: 0 });
const towards = (a: number): Vec2 => ({ x: Math.cos(a), y: Math.sin(a) });

function nearestOpponentDist(state: MatchState, p: PlayerState): number {
  let best = Infinity;
  for (const o of state.players) {
    if (o.team === p.team || o.sentOff) continue;
    best = Math.min(best, dist(p.pos, o.pos));
  }
  return best;
}

/** D39: is the ball→target passing lane free of opponents (none within LANE_BLOCK_DIST of the segment)? */
export function laneOpen(state: MatchState, from: Vec2, to: Vec2, team: number, cfg: SimConfig): boolean {
  const gx = to.x - from.x;
  const gy = to.y - from.y;
  const len = Math.hypot(gx, gy);
  if (len < 1e-6) return true;
  const ux = gx / len;
  const uy = gy / len;
  for (const o of state.players) {
    if (o.team === team || o.sentOff) continue;
    const rx = o.pos.x - from.x;
    const ry = o.pos.y - from.y;
    const along = rx * ux + ry * uy;
    if (along <= 0 || along >= len) continue;
    // scaled like laneClear: tight near the passer (the ball leaves at full pace — a defender at the
    // boot can't react), widening downfield where the pass is slow enough to be stepped into
    const block = cfg.ai.LANE_BLOCK_DIST * (0.35 + 0.65 * (along / len));
    if (Math.abs(rx * uy - ry * ux) < block) return false; // parked on the lane
  }
  return true;
}

/** Best pass target (D39: lane-aware): the most-open goal-side teammate whose LANE is actually clean —
 *  an open man behind a wall of legs is not a pass. Used by the danger-defusal hard branch (the utility
 *  brain scores its own lanes). */
function bestPass(state: MatchState, carrier: PlayerState, goalX: number, cfg: SimConfig, avoid: EntityId | null = null): PlayerState | null {
  const dir = Math.sign(goalX - carrier.pos.x) || 1;
  let best: PlayerState | null = null;
  let bestScore = -Infinity;
  let avoided: PlayerState | null = null; // D59: the feeder is the LAST resort, never the first reflex
  for (const t of state.players) {
    if (t.team !== carrier.team || t.id === carrier.id || t.sentOff || t.isGk) continue;
    if (dir * (t.pos.x - carrier.pos.x) < 1) continue; // must progress toward goal
    if (!laneOpen(state, carrier.pos, t.pos, carrier.team, cfg)) continue;
    const openness = nearestOpponentDist(state, t);
    if (openness <= 1.5) continue;
    if (t.id === avoid && !t.calling) {
      avoided = t; // qualified, but he just fed me — only if NOTHING else exists (a CALL exempts)
      continue;
    }
    const progression = dir * (t.pos.x - carrier.pos.x);
    const score = openness + 0.3 * progression;
    if (score > bestScore) {
      bestScore = score;
      best = t;
    }
  }
  return best ?? avoided;
}

/** The ball is exposed to a stand tackle: near the chaser and on the far side of the carrier (§8.1).
 *  `reachMul` (D46) scales the DECISION reach only — the sim's tackle resolution is never touched. */
function ballExposed(chaser: PlayerState, carrier: PlayerState, ball: Vec2, cfg: SimConfig, reachMul = 1): boolean {
  if (dist(chaser.pos, ball) > cfg.ai.TACKLE_EXPOSED_DIST * reachMul) return false;
  const toChaser = angleTo(carrier.pos, chaser.pos);
  let d = (toChaser - carrier.facing) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  else if (d < -Math.PI) d += Math.PI * 2;
  return Math.abs(d) > Math.PI / 2; // carrier facing away from the chaser
}

/** Nearest opponent within `radius` that is mid tackle (an active lunge to dive into) — else null. */
function opponentMidTackle(state: MatchState, p: PlayerState, radius: number): PlayerState | null {
  for (const o of state.players) {
    if (o.team === p.team || o.sentOff) continue;
    if (o.action !== 'TACKLE_STAND' && o.action !== 'TACKLE_SLIDE') continue;
    if (dist(o.pos, p.pos) <= radius) return o;
  }
  return null;
}

/** The most-open teammate (largest distance to its nearest opponent) — the safe restart outlet (D38).
 *  D55 `requireLane`: only mates on a CLEAN lane qualify (falls back to any mate if nobody is). */
function mostOpenMate(state: MatchState, p: PlayerState, cfg?: SimConfig): PlayerState | null {
  let best: PlayerState | null = null;
  let bestOpen = -Infinity;
  let bestClean: PlayerState | null = null;
  let bestCleanOpen = -Infinity;
  for (const t of state.players) {
    if (t.team !== p.team || t.id === p.id || t.sentOff) continue;
    const open = nearestOpponentDist(state, t);
    if (open > bestOpen) {
      bestOpen = open;
      best = t;
    }
    if (cfg && open > bestCleanOpen && laneOpen(state, p.pos, t.pos, p.team, cfg)) {
      bestCleanOpen = open;
      bestClean = t;
    }
  }
  return bestClean ?? best;
}

/** D55: how CLEAN is the lane from→to — the smallest opponent perpendicular distance to the segment
 *  (∞ if untouched). Deadline feeds pick the least-blocked human instead of the nearest-blocked one. */
function laneClearance(state: MatchState, from: Vec2, to: Vec2, team: number): number {
  const gx = to.x - from.x;
  const gy = to.y - from.y;
  const len = Math.hypot(gx, gy);
  if (len < 1e-6) return Infinity;
  const ux = gx / len;
  const uy = gy / len;
  let worst = Infinity;
  for (const o of state.players) {
    if (o.team === team || o.sentOff) continue;
    const rx = o.pos.x - from.x;
    const ry = o.pos.y - from.y;
    const along = rx * ux + ry * uy;
    if (along <= 0 || along >= len) continue;
    worst = Math.min(worst, Math.abs(rx * uy - ry * ux));
  }
  return worst;
}

/** D44: aim at where a HUMAN target will be (pos + vel × PASS_LEAD_HUMAN_SEC) so the ball lands in
 *  stride, not at his heels — the bot's declared aim now matches the sim soft-lock's own lead. */
function leadAim(from: PlayerState, target: PlayerState, cfg: SimConfig): number {
  const lead = cfg.ai.PASS_LEAD_HUMAN_SEC;
  return angleTo(from.pos, { x: target.pos.x + target.vel.x * lead, y: target.pos.y + target.vel.y * lead });
}

/** D46 Items 3+4: apply the team's difficulty profile to a bot's decision. Reaction/tackle-reach/marking
 *  scale in-core (below); aim noise + pass mis-hit + the move-speed cap are baked in here, ONCE per kick
 *  commit, so the fired kick (later, from the continue-charge block) carries the imperfection. IDENTITY
 *  (level 3) is a bit-for-bit no-op — the existing AI tests exercise this path unchanged. */
/** D88 free-kick wall: the closest 2–3 outfield defenders line up shoulder-to-shoulder on the ball→own-goal
 *  line at the restart-clearance ring, so a direct free kick must be bent/lifted over. Returns the wall
 *  slot for `p`, or null if he isn't a wall member (or the free kick isn't near our goal). */

/** D115: what a carrier does while he is NOT allowed to kick (the settle window, the commit hold). In
 *  space he carries the ball goal-ward; under pressure he stands it up and faces the goal instead —
 *  driving blindly into a set defender is how the D40 body-block strips a receiver, and a player taking
 *  a touch under pressure doesn't run at anyone, he looks up. */
function takeATouch(state: MatchState, p: PlayerState, goalX: number, cfg: SimConfig): PlayerInput {
  const a = angleTo(p.pos, { x: goalX, y: p.pos.y * 0.5 });
  const pressed = nearestOpponentDist(state, p) < cfg.ai.PRESSURE_DIST;
  return input(pressed ? { x: 0, y: 0 } : towards(a), a, 0);
}

export function actPlayer(state: MatchState, id: EntityId, plan: TeamPlan, mem: BotMemory, rng: RngHolder, cfg: SimConfig): PlayerInput {
  const prof = plan.profile ?? IDENTITY_PROFILE;
  const hadKick = mem.kick !== null;
  let inp = actPlayerCore(state, id, plan, mem, rng, cfg, prof);
  if (!hadKick && mem.kick !== null) {
    // D116: two draws, in this fixed order, exactly as before — only what they scale has changed.
    mem.kick.aim += aimNoise(prof, rng, cfg); // ± the baseline, tightened below it and opened above
    if ((mem.kick.button & (BTN.PASS | BTN.THROUGH)) !== 0) {
      // the mis-hit PASS. The soft-lock will rewrite this kick's yaw and its power, so elevation is the
      // only thing a bot can actually get wrong about it — a scuffed pass comes off the deck.
      // Clamped to the legal [0,1] input range HERE, not left to the sim's own clamp01: T-AI-007 and the
      // D114 sweep both pin that every AI-emitted PlayerInput is in range, and a bot that emits an
      // out-of-range value is a bot that is not playing the game through the same door a human uses.
      mem.kick.aimElev = Math.max(0, Math.min(1, (mem.kick.aimElev ?? 0) + passElevNoise(prof, rng, cfg)));
    }
    // reflect both on the windup tick. PASS/THROUGH fire on this very tick under D49 AUTO_POWER, so for
    // them this input IS the kick; SHOOT charges on and picks the aim up again from `mem.kick` below.
    inp = { ...inp, aimYaw: mem.kick.aim, aimElev: mem.kick.aimElev ?? inp.aimElev };
  }
  // D136: the baseline itself caps off-ball pace — bots jog rather than emit unit vectors everywhere
  // ("always fresh, always at pace"). L4/L5 raise chaseSpeedCap above 1 to buy it back. Composed, never
  // greater than 1, and the SPRINT bit is untouched: this is the move vector, not the sprint latch.
  const cap = Math.min(1, cfg.ai.BASE_MOVE_CAP * prof.chaseSpeedCap);
  if (cap < 1) inp = { ...inp, moveX: inp.moveX * cap, moveY: inp.moveY * cap };
  return inp;
}

function actPlayerCore(state: MatchState, id: EntityId, plan: TeamPlan, mem: BotMemory, rng: RngHolder, cfg: SimConfig, prof: DifficultyProfile): PlayerInput {
  const p = state.players[id];
  if (!p || p.sentOff) return input({ x: 0, y: 0 }, 0, 0); // no input for a sent-off player
  const ball = state.ball;
  const size = state.mode.teamSize;
  const dir = state.attackDir[p.team];
  const goalX = (dir * PITCH[size].length) / 2;

  trackPossessionSpell(state, p, mem, cfg); // D59: who fed me this spell (return-pass suppression)

  // continue an in-progress charged kick (don't re-decide mid-windup)
  if (p.charge !== null && mem.kick !== null) {
    const k = mem.kick;
    if (p.charge.ticks >= k.chargeTarget) {
      mem.kick = null; // release this tick → the kick fires
      return input(towards(angleTo(p.pos, { x: goalX, y: 0 })), k.aim, 0);
    }
    return input(towards(angleTo(p.pos, { x: goalX, y: 0 })), k.aim, k.button);
  }
  // D48: the press never LATCHED — the kick was decided mid-action (tackle recovery, GETUP…), and the
  // sim's buttons are edge-triggered, so a continuously-held button is invisible: the bot dribbled into
  // the net holding an impotent SHOOT. Pulse it (release a tick, press the next) until the windup
  // starts; keep the decision only while the situation that spawned it is alive.
  // D62: …but only while the ball is actually KICKABLE. As a restart taker the old guard pulsed a kick
  // decided before the whistle — zero movement, a stale aim — and, because mem.kick is cleared only
  // below this branch, it could never be reached again. A bot fouled while charging a shot therefore
  // stood twitching for the entire restart window instead of taking the penalty it had just won.
  const kickable = ball.possessor === id || dist(p.pos, ball.pos) <= cfg.actions.kick.REACH;
  // D83: during the ceremony the taker WALKS to the ball but never presses (the ball is untakeable), and
  // any stale pre-whistle latch is dropped so the restart is re-decided at ceremony end.
  if (inCeremony(state, cfg) && state.restart?.taker === id) {
    mem.kick = null;
    if (dist(p.pos, ball.pos) > cfg.actions.kick.REACH * 0.75) {
      const a = angleTo(p.pos, ball.pos);
      return input(towards(a), a, 0);
    }
    return input({ x: 0, y: 0 }, angleTo(p.pos, ball.pos), 0);
  }
  if (mem.kick !== null && kickable && (ball.possessor === id || ((state.phase === 'KICKOFF' || state.phase === 'RESTART_PENDING') && state.restart?.taker === id))) {
    const k = mem.kick;
    return input({ x: 0, y: 0 }, k.aim, state.tick % 2 === 0 ? k.button : 0);
  }
  mem.kick = null;
  if (ball.possessor !== id) mem.util = null; // the standing carrier decision dies with possession (D57)

  // 0 · restart taking (§6.1/D38): the DESIGNATED taker (teleported to the spot by the sim) kicks the
  // ball IN — at the openest teammate, never a blind forward blast that hugs the line out of play.
  // Everyone else holds until the restart is actually taken (the sim gates their touches anyway).
  if (state.phase === 'KICKOFF' || state.phase === 'RESTART_PENDING') {
    const r = state.restart;
    if (r && r.taker === p.id) {
      // D62: walk to the BALL, not to the spot. The sim gates the kick on the real ball
      // (applyKicks: dist(ball, player) > REACH ⇒ the press is silently dropped), while this gate
      // measured the SPOT — and startRestart teleports the taker to 0.6 m from the spot, inside the
      // 0.825 m threshold. So whenever the ball was not actually on the spot the taker believed he
      // was on it forever: he pressed, the sim dropped the press, the D48 pulse re-pressed, and
      // nobody could claim the ball either (resolvePossession is PLAY-gated). The whole match stood
      // still for the full restart window.
      if (dist(p.pos, ball.pos) > cfg.actions.kick.REACH * 0.75) {
        const a = angleTo(p.pos, ball.pos);
        return input(towards(a), a, 0); // fetch the ball wherever it actually is
      }
      // D88 set-piece: a PENALTY (or a FREE_KICK within shooting range) is SHOT at goal with a strong but
      // CONTROLLED charge (~0.9× cap — inside the sweet zone, no overcharge scatter). Profile-scaled aim.
      const pitch = PITCH[state.mode.teamSize];
      const goalX = (state.attackDir[p.team] * pitch.length) / 2;
      const distToGoal = Math.abs(goalX - ball.pos.x);
      if (r.kind === 'PENALTY' || (r.kind === 'FREE_KICK' && distToGoal < pitch.length * 0.28)) {
        // aim TIGHT to a post (a keeper on the line can't reach a well-placed penalty from the spot); the
        // side is a seeded coin-flip so it isn't the same corner every time (unreadable).
        const cornerY = (rngFloat(rng) < 0.5 ? -1 : 1) * (pitch.goalWidth / 2 - 0.18);
        const aim = angleTo(ball.pos, { x: goalX, y: cornerY }) + aimNoise(plan.profile ?? IDENTITY_PROFILE, rng, cfg);
        mem.kick = { button: BTN.SHOOT, aim, chargeTarget: Math.round(cfg.actions.kick.CHARGE_CAP * 0.9), aimElev: 0.12 };
        return { moveX: 0, moveY: 0, aimYaw: aim, aimElev: 0.12, buttons: BTN.SHOOT, emote: 0 };
      }
      const mate = mostOpenMate(state, p, cfg); // D55: lane-aware — never kick the restart into a wall
      const tpos = mate ? mate.pos : { x: 0, y: 0 }; // centre spot as the safe fallback
      const aim = angleTo(p.pos, tpos);
      mem.kick = { button: BTN.PASS, aim, chargeTarget: passChargeFor(dist(p.pos, tpos), cfg, state.conditions) };
      return input({ x: 0, y: 0 }, aim, BTN.PASS);
    }
    // D88 free-kick wall: defenders form up on the ball↔own-goal line at the restart-clearance ring so a
    // direct free kick has to be bent/lifted over them (bots read like football). Only near our goal.
    // D124: the wall is its own module now — a size ladder, near-post bias and join-and-drag, all pure
    // geometry over MatchState (ai/wall.ts). Human-held defenders are never conscripted, and a defending
    // human standing on the line drags the whole wall with him.
    {
      const wall = wallSpotFor(state, p, cfg, plan.humans);
      if (wall) return input(towards(angleTo(p.pos, wall)), angleTo(p.pos, ball.pos), 0);
    }
    // D123: everyone else REPOSITIONS. D83 sold the ceremony as time for players to get organised, and
    // then had them emit zero-move for the whole window — so a restart was ten outfielders standing
    // exactly where the whistle caught them, which is the opposite of what a set piece looks like. They
    // walk to their formation slot (the same targets the open-play shape uses) and stop when they get
    // there. Deterministic: a pure function of state, no rng, no new memory.
    // …but only while the CEREMONY runs. Once the whistle has gone the restart is live football again:
    // walking to a formation slot through the take window pulls your teammates AWAY from the ball just
    // as the taker looks for someone to play it to (measurably — it halved the give-and-go rate in the
    // 3v3 kickabout feed test). Post-whistle they hold and make themselves available, as before.
    const slot = inCeremony(state, cfg) ? plan.formationTargets.get(id) : undefined;
    if (slot && dist(p.pos, slot) > RESTART_SHAPE_DIST) {
      return input(towards(angleTo(p.pos, slot)), angleTo(p.pos, ball.pos), 0); // walk there, eyes on the ball
    }
    return input({ x: 0, y: 0 }, angleTo(p.pos, ball.pos), 0); // in position ⇒ hold and watch the ball
  }

  // 1 · hasBall
  if (ball.possessor === id) {
    // D39/D55 danger defusal — HIGHEST priority: the ball near the OWN goal gets moved AWAY, period.
    // In the own box: always (never dwell there); in the own THIRD: under pressure. First choice is a
    // clean pass out (retention); else a first-time blast to the emptier flank.
    const ownThird = dir * p.pos.x < -PITCH[size].length / 6;
    if (inOwnBox(state, p) || (ownThird && nearestOpponentDist(state, p) < cfg.ai.CLEAR_PRESSURE_DIST)) {
      // free man with a clean lane? play OUT of danger to feet first (designer: "pass asap to a free
      // player OR just shoot it out") — retention beats a blind blast when it's actually safe
      const out = bestPass(state, p, goalX, cfg, mem.receivedFrom); // D59: defusal never reflex-returns either
      if (out) {
        const aim = angleTo(p.pos, out.pos);
        mem.kick = { button: BTN.PASS, aim, chargeTarget: passChargeFor(dist(p.pos, out.pos), cfg, state.conditions) };
        return input({ x: 0, y: 0 }, aim, BTN.PASS);
      }
      const flankY = ((): number => {
        const half = PITCH[size].width / 2;
        let bestY = 0;
        let bestOpen = -Infinity;
        for (const y of [-half * 0.6, half * 0.6]) {
          let near = Infinity;
          for (const o of state.players) {
            if (o.team === p.team || o.sentOff) continue;
            near = Math.min(near, Math.hypot(o.pos.x - p.pos.x * 0.5, o.pos.y - y));
          }
          if (near > bestOpen) {
            bestOpen = near;
            bestY = y;
          }
        }
        return bestY;
      })();
      const aim = angleTo(p.pos, { x: dir * PITCH[size].length * 0.25, y: flankY });
      mem.kick = { button: BTN.SHOOT, aim, chargeTarget: 2 };
      return input({ x: 0, y: 0 }, aim, BTN.SHOOT);
    }
    // D115 · SETTLE — the first touch. Until the reception has settled the bot may not kick at all: he
    // carries the ball goal-ward and looks up, which is the beat a human needs and the bot never took.
    // Deliberately BELOW the defusal branch above (a panic clearance at your own goal stays a reflex)
    // and ABOVE everything else, including the D48 in-box shot — receiving in the box and firing on the
    // same tick is exactly the "shoots instantly" the wave exists to kill.
    if (mem.gainTick !== null && state.tick - mem.gainTick < settleTicks(id, mem.gainTick, prof, cfg)) {
      mem.util = null; // decide FRESH when the touch is done — not from a snapshot taken mid-reception
      return takeATouch(state, p, goalX, cfg);
    }

    // shot geometry (hoisted — the box-shot rule below and the ranged xg shot share it). D18: aim at
    // the corner FARTHEST from the keeper (never the covered centre).
    const keeper = state.players.find((o) => o.team !== p.team && !o.sentOff && o.isGk);
    const halfGoal = PITCH[size].goalWidth / 2;
    const cornerY = keeper ? (keeper.pos.y >= 0 ? -1 : 1) * (halfGoal - 0.6) : 0;
    const shotTarget = { x: goalX, y: cornerY };
    const inOppBox =
      dir * (goalX - p.pos.x) >= 0 && dir * (goalX - p.pos.x) <= PITCH[size].penaltyBoxDepth && Math.abs(p.pos.y) <= PITCH[size].penaltyBoxHalfWidth;
    const urgent = inOppBox && keeper != null && dist(p.pos, keeper.pos) < cfg.ai.SHOOT_URGENT_KEEPER_DIST;

    // A2 · D48: IN THE OPPONENT BOX the only play is a SHOT (hard rule — walk-ins are never football).
    if (inOppBox) {
      const aim = angleTo(p.pos, shotTarget);
      mem.kick = { button: BTN.SHOOT, aim, chargeTarget: urgent ? 6 : 8 };
      return input(towards(aim), aim, BTN.SHOOT);
    }

    // D57-A · the UTILITY BRAIN — every option scored every think (see utilityCarrier.ts). Replaces
    // the old caller/human-support/xg-shot/pass-under-pressure/dribble-veer branch chain: the D42/D44
    // doctrines live on as score multipliers (HUMAN_MUL, CALLER_MUL), the D48 blocker-veer as scored
    // dribble DIRECTIONS. Decisions are held for UTIL.PERIOD ticks (plus inertia) so bots commit.
    if (mem.util === null || state.tick >= mem.util.until || (mem.util.choice.target !== null && (state.players[mem.util.choice.target]?.sentOff ?? true))) {
      // D59: within RETURN_TICKS of gaining the ball, the mate who fed me is a suppressed target —
      // no instant ping-pong back to the passer (he calls, or he moves, or he waits).
      const returnTo = mem.receivedFrom !== null && mem.gainTick !== null && state.tick - mem.gainTick < cfg.ai.UTIL.RETURN_TICKS ? mem.receivedFrom : null;
      const lastKey = mem.util?.choice.key ?? null;
      const choice = decideCarrier(state, id, plan, cfg, prof, rng, lastKey, returnTo);
      // D115: a CHANGED mind needs a beat before it becomes an action. Only a switch to a KICK is held
      // (re-aiming a dribble is a step, not a decision), and only when there WAS a standing choice —
      // the first decision of a spell is free, because the settle above already was the thinking time.
      const swerve = lastKey !== null && choice.key !== lastKey && choice.kind !== 'dribble';
      mem.util = {
        choice,
        until: state.tick + Math.max(1, Math.round(cfg.ai.UTIL.PERIOD * prof.reactionMul)),
        readyAt: swerve ? state.tick + Math.round(cfg.ai.UTIL.COMMIT_TICKS * prof.reactionMul) : state.tick,
      };
    }
    const ch = mem.util.choice;
    if (state.tick < mem.util.readyAt) return takeATouch(state, p, goalX, cfg); // decided, not yet acting
    switch (ch.kind) {
      case 'shoot': {
        const aim = angleTo(p.pos, shotTarget); // live corner pick — the keeper moves
        mem.kick = { button: BTN.SHOOT, aim, chargeTarget: SHOOT_CHARGE };
        return input(towards(aim), aim, BTN.SHOOT);
      }
      case 'pass':
      case 'through':
      case 'lob':
      case 'cross': {
        const t = state.players[ch.target!]!;
        const aim = leadAim(p, t, cfg); // live lead — the receiver moves
        // D114: a LOB and a CROSS are a THROUGH with the loft modifier held — literally the two bits a
        // human holds for V+R. The sim's own ladder does the rest, so there is no bot-only aiming path.
        const btn = ch.kind === 'pass' ? BTN.PASS : ch.kind === 'through' ? BTN.THROUGH : BTN.THROUGH | BTN.LOFT;
        mem.kick = { button: btn, aim, chargeTarget: passChargeFor(dist(p.pos, t.pos), cfg, state.conditions) };
        return input({ x: 0, y: 0 }, aim, btn);
      }
      case 'clear': {
        mem.kick = { button: BTN.SHOOT, aim: ch.dir, chargeTarget: 2 };
        return input({ x: 0, y: 0 }, ch.dir, BTN.SHOOT);
      }
      case 'dribble':
      default: {
        // D132 (§3.2) — a bot carrier with OPEN SPACE AHEAD puts his head down and sprints.
        //
        // The dribble input carried no SPRINT bit, and the D39 knock-on gates on the sprint LATCH — so
        // the ball stayed spring-glued to a bot forever while a human's own sprint donated a loose
        // window every 14 ticks. That asymmetry is most of "defending against dribbling bots is almost
        // impossible": there was nothing to defend, because there was never a touch to win. Gated on the
        // same open-ray `utilityCarrier` already scored, so in traffic he keeps his head up (and the
        // knock-on that would gift the ball away does not fire).
        // "Open space" is BOTH: a clear ray along the line he is actually running, AND nobody breathing
        // down his neck. The ray alone is not enough — the utility brain steers the dribble INTO space by
        // construction, so a ray-only gate would have him sprinting out of a crowd with his head down.
        const openAhead =
          spaceAheadOf(state, p, ch.dir) >= cfg.ai.DRIBBLE_SPRINT_OPEN_DIST && nearestOpponentDist(state, p) >= cfg.ai.DRIBBLE_SPRINT_OPEN_DIST;
        return input(towards(ch.dir), angleTo(p.pos, { x: goalX, y: p.pos.y * 0.5 }), openAhead ? BTN.SPRINT : 0);
      }
    }
  }

  // 1.5 · D39: OUR pass is in flight and I am its inferred receiver — sprint to the intercept point
  // (standing still while the pass rolls to a defender was the #1 interception cause).
  if (plan.receiver?.id === id) {
    const pt = plan.receiver.point;
    const aBall = angleTo(p.pos, ball.pos);
    // D111: the delivery is IN THE AIR and will still be up there when we meet ⇒ this is a finish, not a
    // reception. Hold SHOOT from HEADER_ARM_DIST out — the sim's aerial strike (D113) fires it on contact
    // through the SAME input path a human uses, so a bot's header is never a bot-only code path.
    const air = plan.receiver.z;
    const strikeBand = cfg.actions.aerial;
    if (air !== undefined && air >= strikeBand.VOLLEY_Z[0] && air <= strikeBand.HEADER_Z[1]) {
      const finalThird = dir * p.pos.x > PITCH[size].length / 6;
      if (finalThird && dist(p.pos, pt) <= cfg.ai.HEADER_ARM_DIST) {
        // D136: aerial finishes carried ZERO aim noise at every level — they bypassed the noise wrapper
        // entirely, so a bot's header was frame-perfect from L1 to L5. One draw at a fixed point in the
        // per-tick order, so it is replay-safe (bot inputs are logged; replay never re-runs the AI).
        const aimGoal = angleTo(p.pos, { x: goalX, y: 0 }) + aimNoise(prof, rng, cfg);
        const mv = dist(p.pos, pt) > ARRIVE_DIST ? towards(angleTo(p.pos, pt)) : { x: 0, y: 0 };
        return input(mv, aimGoal, BTN.SHOOT); // attack the ball, strike it goal-ward
      }
    }
    // D45 Item 6: within reach of the loose, in-flight pass ⇒ TRAP it (catch/deflect) — the human's verb
    const inFlight = ball.possessor === null && Math.hypot(ball.vel.x, ball.vel.y) > 1;
    if (inFlight && ball.pos.z < cfg.possession.CLAIM_Z * 1.5 && dist(p.pos, ball.pos) < cfg.actions.trap.REACH + 0.5) {
      return input(towards(aBall), aBall, BTN.TRAP);
    }
    if (dist(p.pos, pt) > ARRIVE_DIST) {
      return input(towards(angleTo(p.pos, pt)), aBall, BTN.SPRINT); // D44: SPRINT to the intercept point — a crisp pass outruns a jog
    }
    return input({ x: 0, y: 0 }, aBall, 0); // set, waiting on the ball
  }

  // 1.6 · D114 (HB-E) — CONTEST THE CROSS. Without this the keeper's positioning (D111) is the only thing
  // between a delivery and a free header, which is nowhere near enough: he cannot jump in v1. The nearest
  // outfield defender to an incoming opponent cross that will drop in or around our box arms SHOOT with an
  // UPFIELD aim, and the D113 strike resolves it on contact — the same verb, the same input path, the
  // same contact-quality rules a human gets. `DEF_HEADER: 0` turns it off (a playtest lever).
  if (cfg.ai.DEF_HEADER === 1 && !p.isGk && ball.possessor === null && ball.pos.z > cfg.possession.CLAIM_Z) {
    const theirs = ball.lastTouch !== null && ball.lastTouch.team !== p.team;
    const land = theirs ? ballLanding(ball, cfg, state.conditions) : null;
    if (land !== null) {
      const pitch = PITCH[size];
      const ownGoalX = -dir * (pitch.length / 2);
      const depthIn = dir * (land.x - ownGoalX); // metres from our goal line into the pitch
      if (depthIn >= 0 && depthIn <= pitch.penaltyBoxDepth * 1.5 && Math.abs(land.y) <= pitch.penaltyBoxHalfWidth * 1.2) {
        // the nearest of US to the landing spot is the one who attacks it — everyone else holds shape
        let nearest = -1;
        let bestD = Infinity;
        for (const q of state.players) {
          if (q.team !== p.team || q.sentOff || q.isGk) continue;
          const d = dist(q.pos, land);
          if (d < bestD || (d === bestD && q.id < nearest)) {
            bestD = d;
            nearest = q.id;
          }
        }
        if (nearest === p.id && bestD <= cfg.ai.HEADER_ARM_DIST) {
          // clear it AWAY and WIDE — never square across your own box (the D39 defusal doctrine)
          const flankY = (p.pos.y >= 0 ? 1 : -1) * (pitch.width / 2) * 0.6;
          const clearAim = angleTo(p.pos, { x: dir * pitch.length * 0.3, y: flankY });
          const mv = dist(p.pos, land) > ARRIVE_DIST ? towards(angleTo(p.pos, land)) : { x: 0, y: 0 };
          return input(mv, clearAim, BTN.SHOOT);
        }
      }
    }
  }

  // 3-4 · misconduct temptations (§8.1): each rolls aiRng once on its rising edge (latched), acted on in a
  // fixed order so the draw sequence is deterministic. Evaluated before pressing so a beaten bot can foul.
  {
    const clarityOnMe = refClarity(state.referee.pos, state.referee.viewDir, p.pos, state, new Set<EntityId>([id]), cfg);
    const refBlind = clarityOnMe < cfg.ai.REF_BLIND_MAX;
    const aggr = mem.lastAggressor;
    const aggP = aggr && state.tick - aggr.tick < cfg.ai.GRUDGE_TICKS ? state.players[aggr.id] : undefined;
    const adjacent = aggP != null && !aggP.sentOff && aggP.team !== p.team && dist(p.pos, aggP.pos) < cfg.actions.PUNCH_REACH + 0.5;

    const carrier = ball.possessor !== null ? state.players[ball.possessor] : undefined;
    const ownGoalX = -goalX;
    const beaten = carrier != null && carrier.team !== p.team && dir * (carrier.pos.x - p.pos.x) < 0 && xg(carrier.pos, ownGoalX, cfg) > cfg.ai.TACTICAL_XG_MIN && !inOwnBox(state, p);

    const pitch = PITCH[size];
    const inOppBox = dir * (goalX - p.pos.x) >= 0 && dir * (goalX - p.pos.x) <= pitch.penaltyBoxDepth && Math.abs(p.pos.y) <= pitch.penaltyBoxHalfWidth;
    const tackler = inOppBox ? opponentMidTackle(state, p, cfg.ai.TACKLE_SLIDE_DIST) : null;

    const fireRetaliate = rollOpportunity(mem.latches.retaliate, !!(refBlind && adjacent), cfg.ai.P_RETALIATE, rng, cfg.ai.P_REARM);
    const fireFoul = rollOpportunity(mem.latches.tacticalFoul, !!beaten, cfg.ai.P_TACTICAL_FOUL, rng, cfg.ai.P_REARM);
    const fireDive = rollOpportunity(mem.latches.dive, tackler != null, cfg.ai.aggression * cfg.ai.P_DIVE_MUL, rng, cfg.ai.P_REARM);
    if (fireRetaliate && aggP) {
      const a = angleTo(p.pos, aggP.pos);
      return input(towards(a), a, BTN.PUNCH); // throw hands at the grudge target while the ref is blind
    }
    if (fireFoul && carrier) {
      const a = angleTo(p.pos, carrier.pos);
      return input(towards(a), a, BTN.SLIDE); // cynical late slide to stop a break
    }
    if (fireDive) return input({ x: 0, y: 0 }, p.facing, BTN.DIVE); // go down under a tackle in the box
  }

  // 2 · chaser: press; standing tackle when the ball is exposed — but only after holding the exposure
  // for TACKLE_REACT_TICKS (D32: a human reaction window; turning into the chaser resets it).
  // D38: a TEAMMATE on the ball is never chased — mobbing our own carrier (worst case the keeper) is
  // how bots bundled the ball out or into the own net; shape up in formation instead.
  const chaseCarrier = ball.possessor !== null ? state.players[ball.possessor] : undefined;
  const chasing = plan.chaser === id && !(chaseCarrier && chaseCarrier.team === p.team);
  if (!chasing) mem.tackleReadyTick = null;
  if (chasing) {
    // D111: chase where the ball is GOING, not the point under it. A hanging ball's shadow is a lie —
    // running at it puts you where the ball already isn't by the time you arrive. `ballLanding` is the
    // same prediction the client's landing ring draws, so the bots go where the player sees it coming down.
    const landing = ballLanding(ball, cfg, state.conditions);
    const toBall = angleTo(p.pos, landing ?? ball.pos);
    const carrier = chaseCarrier;
    // D36: a keeper with the ball IN HANDS is untackleable — pressing him is futile mobbing. Hold off
    // outside GK_HOLD_STANDOFF (backing out if too close) and wait for the distribution instead.
    if (carrier && carrier.team !== p.team && carrier.action === 'GK_HOLD') {
      mem.tackleReadyTick = null;
      const standoff = cfg.ai.GK_HOLD_STANDOFF * prof.markingSlackMul; // D46: lazy defenders back off further
      const away = dist(p.pos, carrier.pos) < standoff ? towards(angleTo(carrier.pos, p.pos)) : { x: 0, y: 0 };
      return input(away, toBall, 0);
    }
    // D40: pulled level with a sprinting carrier? shoulder him off it (legal side-on barge)
    if (carrier && carrier.team !== p.team) {
      const cs = Math.hypot(carrier.vel.x, carrier.vel.y);
      const ps = Math.hypot(p.vel.x, p.vel.y);
      const d = dist(p.pos, carrier.pos);
      if (d < cfg.actions.barge.REACH + 0.4 && cs > 3 && ps > 3) {
        const par = (p.vel.x * carrier.vel.x + p.vel.y * carrier.vel.y) / (ps * cs);
        const bearing = Math.abs(Math.cos(angleTo(carrier.pos, p.pos) - carrier.facing));
        if (par > cfg.actions.barge.PAR_DOT && bearing <= cfg.actions.barge.SIDE_DOT) {
          // D136: the barge was a frame-perfect reflex with no difficulty attach point at ANY level.
          // ONE roll per APPROACH — latched, so a bot does not re-roll every tick he stays alongside
          // (which would make P_BARGE 0.6 into a certainty within three ticks).
          if (mem.bargeRoll === null || state.tick - mem.bargeRoll.approachTick > cfg.ai.UTIL.PERIOD) {
            mem.bargeRoll = { approachTick: state.tick, go: rngFloat(rng) < cfg.ai.P_BARGE };
          }
          if (mem.bargeRoll.go) return input(towards(toBall), toBall, BTN.SHOVE);
        }
      } else if (mem.bargeRoll !== null && dist(p.pos, carrier.pos) > cfg.actions.barge.REACH * 2) {
        mem.bargeRoll = null; // he broke off — the NEXT approach is a genuinely new one
      }
    }
    // D48: at GRIND range (capsules in contact, the carrier pushing into me) a front tackle is the
    // duel-breaker. D50 balance: only when the BALL is actually pokeable (near MY side, not parked on
    // the carrier's far side) and never vs a SHIELDing carrier (frontal tackles just bounce off the
    // shield — D35 duel geometry). Turning your body between defender and ball now genuinely protects
    // it, which is the designed counterplay ("bots steal EVERY time" was this check missing).
    const shielded = carrier !== undefined && carrier.action === 'SHIELD';
    const grinding =
      carrier &&
      carrier.team !== p.team &&
      !shielded &&
      dist(p.pos, carrier.pos) < cfg.collision.CAPSULE_R * 2 + 0.3 &&
      dist(p.pos, ball.pos) < cfg.actions.tackle.STAND_REACH * 0.8;
    if (carrier && carrier.team !== p.team && (grinding || (!shielded && ballExposed(p, carrier, ball.pos, cfg, prof.tackleReachMul)))) {
      // D46: slower bots read the exposed touch later (reaction ×); faster bots pounce sooner
      if (mem.tackleReadyTick === null) mem.tackleReadyTick = state.tick + Math.round(cfg.ai.TACKLE_REACT_TICKS * prof.reactionMul);
      if (state.tick >= mem.tackleReadyTick) {
        mem.tackleReadyTick = null; // re-arm: the next attempt needs a fresh exposure window
        // D136 — the DUEL MISS. This is D116's open contest-roll item, resolved in the AI layer exactly
        // as PT4-P6 requires: the SIM stays difficulty-free and a human's legally-placed tackle still
        // always wins. What varies by level is whether the BOT commits at the right moment. One draw per
        // armed exposure, on the tick it would have fired.
        if (rngFloat(rng) < cfg.ai.DUEL_MISS_P * prof.duelMissP) return input(towards(toBall), toBall, 0);
        return input(towards(toBall), toBall, BTN.TACKLE);
      }
      return input(towards(toBall), toBall, 0); // closing in, reading the touch…
    }
    mem.tackleReadyTick = null;
    // D45 Item 6: chasing a LOOSE, moving ball within reach ⇒ TRAP it — but NEVER intercept our own
    // team's pass (that's the designated receiver's ball; re-trapping your own kick strands the human).
    const kicker = ball.kickedBy !== null ? state.players[ball.kickedBy] : undefined;
    const ourPass = kicker !== undefined && kicker.team === p.team;
    // D111: …and only a ball he can actually reach with a foot — trapping tops out at the same ceiling
    // resolveTrap uses, so pressing F under a ball at head height was always a whiff.
    if (!carrier && !ourPass && ball.pos.z < cfg.possession.CLAIM_Z * 1.5 && Math.hypot(ball.vel.x, ball.vel.y) > 1 && dist(p.pos, ball.pos) < cfg.actions.trap.REACH + 0.5) {
      // D136: a TRAP was a frame-perfect reflex — no reaction delay at any level. A beat first.
      if (mem.trapReadyTick === null) mem.trapReadyTick = state.tick + Math.round(cfg.ai.TRAP_REACT_TICKS * prof.reactionMul);
      if (state.tick < mem.trapReadyTick) return input(towards(toBall), toBall, 0);
      mem.trapReadyTick = null;
      return input(towards(toBall), toBall, BTN.TRAP);
    }
    mem.trapReadyTick = null;
    // D132: pace is honest both ways — a chaser a long way off the ball sprints after it, and pays the
    // stamina for it like anybody else. Close in, he does not: a sprint into a duel is how you overrun it.
    const chaseSprint = dist(p.pos, landing ?? ball.pos) > cfg.ai.CHASE_SPRINT_DIST ? BTN.SPRINT : 0;
    return input(towards(toBall), toBall, chaseSprint);
  }

  // 5 · default: arrive at the formation target, face the ball, CALL when open (evaluators 3–4 are M5).
  // D55 get-open: while MY team attacks, shade the formation spot a few metres toward whichever side
  // gives the carrier the cleanest lane to me — off-ball mates make themselves PASSABLE, which is most
  // of what reads as "intelligent" support play.
  let target = plan.formationTargets.get(id) ?? p.pos;
  const carrier = ball.possessor !== null ? state.players[ball.possessor] : undefined;
  if (plan.phase === 'ATTACK' && carrier && carrier.team === p.team && carrier.id !== id) {
    const lx = target.x - carrier.pos.x;
    const ly = target.y - carrier.pos.y;
    const ll = Math.hypot(lx, ly) || 1;
    const px = -ly / ll;
    const py = lx / ll;
    let bestT = target;
    let bestC = laneClearance(state, carrier.pos, target, p.team);
    for (const off of [-3, 3]) {
      const cand = { x: target.x + px * off, y: target.y + py * off };
      const c = laneClearance(state, carrier.pos, cand, p.team);
      if (c > bestC) {
        bestC = c;
        bestT = cand;
      }
    }
    target = bestT;
  }
  const faceBall = angleTo(p.pos, ball.pos);
  const mv = dist(p.pos, target) > ARRIVE_DIST ? towards(angleTo(p.pos, target)) : { x: 0, y: 0 };
  const open = nearestOpponentDist(state, p) > cfg.ai.CALL_OPEN_DIST;
  return input(mv, faceBall, open ? BTN.CALL : 0);
}
